import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumericValue } from 'utils/general';

import { TaskHeader } from '@/modules/home';
import { IHome } from '@/modules/home/types';
import { Avatar, Typography } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import ProgressBar from '@/shared/components/atoms/ProgressBar';
import { EntityType } from '@/shared/constant/enums';
import routes from '@/shared/constant/routes';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import Box from '~/images/shared/box.svg';
import TargetIcon from '~/images/shared/cup.svg';
import FarmerIcon from '~/images/shared/Farmer.svg';

interface IProps {
  technicalTask: IHome.ITimelineOperationTechnicalItem;
}

const getProgressBarColor = (achieved: number, target: number): string => {
  if (achieved < (target * 50) / 100) {
    return 'bg-error-95';
  }
  if (achieved >= (target * 50) / 100 && achieved < target) {
    return 'bg-orange-500';
  }
  return 'bg-secondary-60';
};

const TechnicalTask = ({ technicalTask }: IProps): ReactElement => {
  const { t } = useTranslation();
  const { push } = useRouter();
  const { openModal } = useModal();
  const [store] = useStore((store) => store);

  const isFarm = store?.asset?.entityTypeId === EntityType.FARM;

  return (
    <div
      onClick={() =>
        store?.isDemoFarm
          ? openModal(<DemoCTA />)
          : push(`${routes.workingDay}/${technicalTask.id}`)
      }
      className='flex cursor-pointer gap-12 px-16'
    >
      <div className='shrink-0'>
        <Avatar
          className='  bg-blue-100'
          size='sm'
          icon={isFarm ? <FarmerIcon /> : <Box />}
        />
      </div>
      <div className='flex flex-grow flex-col gap-12'>
        {/* task name and creator */}
        <TaskHeader
          createdBy={technicalTask.createdBy}
          name={technicalTask.name}
          plots={technicalTask.plots}
          timestamp={technicalTask.updatedAt}
        />

        {/* target progress  */}
        <div className='inline-flex items-center gap-8'>
          <div>
            <TargetIcon />
          </div>
          <Typography variant='subtitle2'>{t('target')}</Typography>

          <ProgressBar
            value={technicalTask?.productivityAchieved}
            displayValue={formatNumericValue(
              technicalTask.productivityAchieved
            )}
            // showValue={technicalTask.status !== 'todo'}
            showValue={Boolean(technicalTask.productivityAchieved)}
            max={technicalTask?.productivityTarget}
            barBgColor={
              technicalTask.status === 'todo'
                ? 'bg-neutralVariant-90'
                : getProgressBarColor(
                    technicalTask?.productivityAchieved || 0,
                    technicalTask?.productivityTarget || 0
                  )
            }
          />
          <Typography
            variant='caption'
            weight='bold'
            className='whitespace-nowrap'
          >
            {technicalTask.productivityTarget
              ? formatNumericValue(technicalTask.productivityTarget)
              : ''}
            {technicalTask.measurementUnit}
          </Typography>
        </div>
      </div>
    </div>
  );
};
export { TechnicalTask };
